import {
    Fragment,
    useEffect,
    useState
} from "react";

import {
    IBarDateChartData,
    IExampleStats,
    IExampleStatsForTemplate,
    IExampleTags,
    IExampleTagsStats,
    IExampleUserStats,
    IOperationEndpointJobStats,
    IOperationEndpointUserStats,
    IOperationJobStats,
    IOperationStatsForTemplate,
    IStatsRange,
    ITemplateOnboarding,
    IUserSlim
} from "../lib/backend/extractions.types.generated";
import {
    classNames,
    getISODateFromNumbers,
    sumArray
} from "../lib/utils";
import {
    TEMPLATE_EXAMPLE_TAGS,
    TIME_RANGES
} from "../lib/consts";

import {
    BarChart,
    Indicator,
    StackedBarChart
} from "./Chart";
import {
    ButtonGroup
} from "./Button";
import { LoadingSpinnerLimit } from "./LoadingSpinner";

function getDateRange(range: IStatsRange): string[] {
    const dates: string[] = [];
    const now_date_ts = new Date().getTime();
    const oldest_date_ts = now_date_ts - (range === "7d" ? 6 : 29) * TIME_RANGES.DAY;
    let oldest_date_ts_iter = oldest_date_ts;
    while (oldest_date_ts_iter <= now_date_ts) {
        const date = new Date(oldest_date_ts_iter);
        dates.push(getISODateFromNumbers(date.getFullYear(), date.getMonth() + 1, date.getDate()));
        oldest_date_ts_iter += TIME_RANGES.DAY;
    }
    return dates;
}

function getDateCount(stats: IExampleStats[], range: IStatsRange): IBarDateChartData[] {
    const date_range = getDateRange(range);
    const date_count_map = new Map<string, number>();
    for (const date of date_range) {
        date_count_map.set(date, 0);
    }
    for (const stat of stats) {
        if (date_count_map.has(stat.date)) {
            const old_count = date_count_map.get(stat.date) ?? 0;
            date_count_map.set(stat.date, old_count + stat.count);
        }
    }

    // convert map to array
    const date_count = Array.from(date_count_map.entries()).map(([date, count]) => ({ date, count }));
    return date_count;
}

function getTagsCount(stats: IExampleTagsStats[], range: IStatsRange | "all", onboarding?: ITemplateOnboarding): { tag: string, count: number, color?: "green" | "red" }[] {
    const date_range = range === "all" ? [] : getDateRange(range);

    const green_tags = onboarding?.step === "build" ? onboarding.build.tags.green_tags :
        onboarding?.step === "onboarding" && onboarding.onboarding ? onboarding.onboarding.tags.green_tags :
            onboarding?.step === "operation" && onboarding.operation ? onboarding.operation.tags.green_tags :
                [TEMPLATE_EXAMPLE_TAGS.fixed, TEMPLATE_EXAMPLE_TAGS.no_issue, TEMPLATE_EXAMPLE_TAGS.will_not_fix, TEMPLATE_EXAMPLE_TAGS.cannot_reproduce];
    const red_tags = onboarding?.step === "build" ? onboarding.build.tags.red_tags :
        onboarding?.step === "onboarding" && onboarding.onboarding ? onboarding.onboarding.tags.red_tags :
            onboarding?.step === "operation" && onboarding.operation ? onboarding.operation.tags.red_tags :
                [TEMPLATE_EXAMPLE_TAGS.new_example, TEMPLATE_EXAMPLE_TAGS.in_progress, TEMPLATE_EXAMPLE_TAGS.does_not_work, TEMPLATE_EXAMPLE_TAGS.needs_feedback];

    // group count by tag
    const tags_count_map = new Map<string, number>();
    for (const { date, tag, count } of stats) {
        if (onboarding?.step === "build" && tag === "Build") { continue; }
        if (onboarding?.step === "onboarding" && tag === "Onboarding") { continue; }
        if (range === "all" || date_range.includes(date)) {
            tags_count_map.set(tag, (tags_count_map.get(tag) ?? 0) + count);
        }
    }

    // convert map to array
    const tags_count = Array.from(tags_count_map.entries()).map(([tag, count]) => ({
        tag,
        count,
        color: (green_tags.includes(tag) ? "green" : red_tags.includes(tag) ? "red" : undefined) as "green" | "red" | undefined
    }));
    tags_count.sort((a, b) => b.count - a.count);
    return tags_count.slice(0, 20);
}

function getExamplesCount(examples_tags: IExampleTags[], onboarding?: ITemplateOnboarding): { green: number, red: number, total: number } {
    const green_tags = onboarding?.step === "build" ? onboarding.build.tags.green_tags :
        onboarding?.step === "onboarding" && onboarding.onboarding ? onboarding.onboarding.tags.green_tags :
            onboarding?.step === "operation" && onboarding.operation ? onboarding.operation.tags.green_tags :
                [TEMPLATE_EXAMPLE_TAGS.fixed, TEMPLATE_EXAMPLE_TAGS.no_issue, TEMPLATE_EXAMPLE_TAGS.will_not_fix, TEMPLATE_EXAMPLE_TAGS.cannot_reproduce];
    const red_tags = onboarding?.step === "build" ? onboarding.build.tags.red_tags :
        onboarding?.step === "onboarding" && onboarding.onboarding ? onboarding.onboarding.tags.red_tags :
            onboarding?.step === "operation" && onboarding.operation ? onboarding.operation.tags.red_tags :
                [TEMPLATE_EXAMPLE_TAGS.new_example, TEMPLATE_EXAMPLE_TAGS.in_progress, TEMPLATE_EXAMPLE_TAGS.does_not_work, TEMPLATE_EXAMPLE_TAGS.needs_feedback];

    let green_examples = 0;
    let red_examples = 0;
    for (const example_tags of examples_tags) {
        // one red is enough to be red
        if (example_tags.tags.some(tag => red_tags.includes(tag))) {
            red_examples++;
        } else if (example_tags.tags.some(tag => green_tags.includes(tag))) {
            green_examples++;
        }
    }

    return { green: green_examples, red: red_examples, total: examples_tags.length };
}

function getExampleUserCount(stats: IExampleUserStats[], range: IStatsRange| "all"): { user_full_name: string, count: number }[] {
    const date_range = range === "all" ? [] : getDateRange(range);
    // group count by user
    const user_map = new Map<string, IUserSlim>();
    const user_count_map = new Map<string, number>();
    for (const stat of stats) {
        if (range === "all" || date_range.includes(stat.date)) {
            user_map.set(stat.user.uuid, stat.user);
            user_count_map.set(stat.user.uuid, (user_count_map.get(stat.user.uuid) ?? 0) + stat.count);
        }
    }

    // pretty print user name
    const getUserFullName = (user_uuid: string): string => {
        const user = user_map.get(user_uuid);
        if (!user) {
            return "Unknown";
        }
        return `${user.first_name} ${user.last_name}`;
    }

    // convert map to array
    const user_count = Array.from(user_count_map.entries()).map(([user_uuid, count]) => ({ user_full_name: getUserFullName(user_uuid), count }));
    user_count.sort((a, b) => b.count - a.count);

    return user_count;
}

function getOperationEndpointUuids(onboarding?: ITemplateOnboarding): string[] | undefined {
    return onboarding?.step === "operation" && onboarding.operation !== undefined ?
        onboarding.operation.expectations.filter(e => e.type === "operation").map(e => e.endpoint_uuid) :
        undefined;
}

function getOperationEndpointUserCount(
    stats: IOperationEndpointUserStats[],
    range: IStatsRange | "all",
    only_manual: boolean,
    onboarding?: ITemplateOnboarding
): { user_full_name: string, count: number }[] {
    const endpoint_uuids = getOperationEndpointUuids(onboarding);
    // filter stats to manual in case we track TODOs done by users
    const filtered_stats = (only_manual ? stats.filter(s => s.confirmation_source === "manual") : stats)
        // filter stats to only endpoints we track in operation onboarding
        // if not specified, we assume all endpoints are tracked
        .filter(s => endpoint_uuids?.includes(s.endpoint_uuid) || endpoint_uuids === undefined);

    // get all days in range
    const date_range = range === "all" ? [] : getDateRange(range);
    // group count by user
    const user_map = new Map<string, IUserSlim>();
    const user_count_map = new Map<string, number>();
    for (const stat of filtered_stats) {
        if (range === "all" || date_range.includes(stat.date)) {
            user_map.set(stat.user.uuid, stat.user);
            user_count_map.set(stat.user.uuid, (user_count_map.get(stat.user.uuid) ?? 0) + stat.count);
        }
    }

    // pretty print user name
    const getUserFullName = (user_uuid: string): string => {
        const user = user_map.get(user_uuid);
        if (!user) {
            return "Unknown";
        }
        return `${user.first_name} ${user.last_name}`;
    }

    // convert map to array
    const user_count = Array.from(user_count_map.entries()).map(([user_uuid, count]) => ({ user_full_name: getUserFullName(user_uuid), count }));
    user_count.sort((a, b) => b.count - a.count);

    return user_count;
}


function getTagStatsDateCount(stats: IExampleTagsStats[], range: IStatsRange, onboarding?: ITemplateOnboarding): IBarDateChartData[] {
    const date_range = getDateRange(range);
    const date_count_map = new Map<string, { in_progress: number, correct: number }>();
    for (const date of date_range) {
        date_count_map.set(date, { in_progress: 0, correct: 0 });
    }

    const green_tags = onboarding?.step === "build" ? onboarding.build.tags.green_tags :
        onboarding?.step === "onboarding" && onboarding.onboarding ? onboarding.onboarding.tags.green_tags :
            onboarding?.step === "operation" && onboarding.operation ? onboarding.operation.tags.green_tags :
                [TEMPLATE_EXAMPLE_TAGS.fixed, TEMPLATE_EXAMPLE_TAGS.no_issue, TEMPLATE_EXAMPLE_TAGS.will_not_fix, TEMPLATE_EXAMPLE_TAGS.cannot_reproduce];
    const red_tags = onboarding?.step === "build" ? onboarding.build.tags.red_tags :
        onboarding?.step === "onboarding" && onboarding.onboarding ? onboarding.onboarding.tags.red_tags :
            onboarding?.step === "operation" && onboarding.operation ? onboarding.operation.tags.red_tags :
                [TEMPLATE_EXAMPLE_TAGS.new_example, TEMPLATE_EXAMPLE_TAGS.in_progress, TEMPLATE_EXAMPLE_TAGS.does_not_work, TEMPLATE_EXAMPLE_TAGS.needs_feedback];

    for (const stat of stats) {
        if (date_range.includes(stat.date)) {
            const old_count = date_count_map.get(stat.date) ?? { in_progress: 0, correct: 0 };
            date_count_map.set(stat.date, {
                in_progress: old_count.in_progress + (red_tags.includes(stat.tag) ? stat.count : 0),
                correct: old_count.correct + (green_tags.includes(stat.tag) ? stat.count : 0)
            });
        }
    }

    // convert map to array
    const date_count = Array.from(date_count_map.entries()).map(([date, counts]) => ({ date, ...counts }));
    return date_count;
}

function getEndpointJobsDateCount(
    endpoint_job_stats: IOperationEndpointJobStats[],
    range: IStatsRange,
    onboarding?: ITemplateOnboarding
): { jobs_date_count: IBarDateChartData[], jobs_count: number } {
    const endpoint_uuids = getOperationEndpointUuids(onboarding);
    const filtered_stats = endpoint_job_stats.filter(s => endpoint_uuids?.includes(s.endpoint_uuid) || endpoint_uuids === undefined);

    // count jobs by date
    const date_range = getDateRange(range);
    const job_count_map = new Map<string, number>();
    for (const date of date_range) {
        job_count_map.set(date, 0);
    }
    for (const stat of filtered_stats) {
        if (job_count_map.has(stat.date)) {
            job_count_map.set(stat.date, (job_count_map.get(stat.date) ?? 0) + stat.count);
        }
    }

    // convert map to array
    const date_count = Array.from(job_count_map.entries()).map(([date, count]) => ({ date, count }));
    return {
        jobs_date_count: date_count,
        jobs_count: sumArray(date_count.map(d => d.count))
    };
}

function getEndpointJobsTimeSaved(
    confirmation_user_stats: IOperationEndpointUserStats[],
    range: IStatsRange,
    onboarding?: ITemplateOnboarding
): { time_saved?: number } {
    if (onboarding?.step !== "operation" || onboarding.operation === undefined) { return {}; }
    const time_saved_per_endpoint_manual = new Map<string, { manual: number, automatic: number }>();
    for (const expectation of onboarding.operation.expectations) {
        if (expectation.type === "operation") {
            time_saved_per_endpoint_manual.set(expectation.endpoint_uuid, {
                manual: expectation.expected_time_saved_per_manual_job,
                automatic: expectation.expected_time_saved_per_automatic_job
            });
        }
    }

    let time_saved = 0;
    const date_range = getDateRange(range);
    for (const stat of confirmation_user_stats) {
        if (date_range.includes(stat.date)) {
            const time_saved_per_endpoint = time_saved_per_endpoint_manual.get(stat.endpoint_uuid);
            if (time_saved_per_endpoint) {
                const time_saved_per_source = time_saved_per_endpoint[stat.confirmation_source] ?? 0;
                time_saved += time_saved_per_source * stat.count;
            }
        }
    }

    return { time_saved };
}

function getJobsDateCount(
    job_stats: IOperationJobStats[],
    range: IStatsRange
): { jobs_date_count: IBarDateChartData[], jobs_count: number } {
    // count jobs by date
    const date_range = getDateRange(range);
    const job_count_map = new Map<string, number>();
    for (const date of date_range) {
        job_count_map.set(date, 0);
    }
    for (const stat of job_stats) {
        if (job_count_map.has(stat.date)) {
            job_count_map.set(stat.date, (job_count_map.get(stat.date) ?? 0) + stat.count);
        }
    }

    // convert map to array
    const date_count = Array.from(job_count_map.entries()).map(([date, count]) => ({ date, count }));
    return {
        jobs_date_count: date_count,
        jobs_count: sumArray(date_count.map(d => d.count))
    };
}

const MILESTONE_STATUS_WEIGHTS = {
    "completed": 1.0,
    "in_progress": 0.3,
    "not_started": 0.0
}

export function getOnboardingProgressStats(examples_tags: IExampleTags[], onboarding: ITemplateOnboarding): number | undefined {
    if (onboarding.step === "build") {
        // here we have several dimensions:
        //  - number of collected examples compared to target
        //  - number of green examples out of the collected examples
        //  - number of completed milestones
        // result is a weighted sum of these values
        const examples_count = getExamplesCount(examples_tags, onboarding);
        const factor_collected = onboarding.build.no_examples > 0 ? Math.min(examples_count.total / onboarding.build.no_examples, 1.0) : 0;
        const factor_green = onboarding.build.no_examples > 0 ? Math.min(examples_count.green / onboarding.build.no_examples, 1.0) : 0;
        const factor_milestones = sumArray(onboarding.build.milestones.map(m => MILESTONE_STATUS_WEIGHTS[m.status])) / onboarding.build.milestones.length;
        return 0.4 * factor_collected + 0.4 * factor_green + 0.2 * factor_milestones;
    }
    if (onboarding.step === "onboarding" && onboarding.onboarding !== undefined) {
        // here we have several dimensions:
        //  - number of collected examples compared to target
        //  - number of green examples out of the collected examples
        //  - number of completed milestones
        // result is a weighted sum of these values
        const no_users = onboarding.onboarding.responsibilities.users_uuids.length;
        const required_examples_count = no_users * onboarding.onboarding.no_examples_per_user;
        const actual_examples_count = getExamplesCount(examples_tags, onboarding);
        const factor_collected = required_examples_count > 0 ? Math.min(actual_examples_count.total / required_examples_count, 1.0) : 0;
        const factor_green = required_examples_count > 0 ? Math.min(actual_examples_count.green / required_examples_count, 1.0) : 0;
        const factor_milestones = sumArray(onboarding.onboarding.milestones.map(m => MILESTONE_STATUS_WEIGHTS[m.status])) / onboarding.onboarding.milestones.length;
        return 0.4 * factor_collected + 0.4 * factor_green + 0.2 * factor_milestones;
    }
    return undefined;
}

export function getOperationProgressStats(endpoint_job_stats: IOperationEndpointJobStats[], onboarding: ITemplateOnboarding): { jobs_count: number, expected_jobs_count: number } | undefined {
    if (onboarding.step !== "operation" || onboarding.operation === undefined) { return undefined; }
    const jobs_date_count = getEndpointJobsDateCount(endpoint_job_stats, "30d", onboarding); // TODO: fix
    return {
        jobs_count: jobs_date_count.jobs_count,
        expected_jobs_count: sumArray(onboarding.operation.expectations.filter(e => e.type === "operation").map(e => e.expected_no_jobs))
    };
}

function getTimeSavedVal(time_saved_minutes: number): number {
    if (time_saved_minutes < 60) {
        return time_saved_minutes;
    }
    return time_saved_minutes / 60;
}

function getTimeSavedUnit(time_saved_minutes: number): string {
    if (time_saved_minutes < 60) {
        return "min";
    }
    return "h";
}

type TemplateEvalStatsProps = {
    example_stats: IExampleStatsForTemplate;
    operation_stats: IOperationStatsForTemplate;
    onboarding?: ITemplateOnboarding;
}

export function TemplateEvalStats({
    example_stats,
    operation_stats,
    onboarding
}: TemplateEvalStatsProps) {
    const [range, setRange] = useState<IStatsRange>("30d");
    const [examples_count, setExamplesCount] = useState<{ green: number, red: number, total: number } | undefined>(undefined);
    const [examples_user_count, setExamplesUserCount] = useState<{ user_full_name: string, count: number }[] | undefined>(undefined);
    const [examples_tags_count, setExamplesTagsCount] = useState<{ tag: string, count: number, color?: "green" | "red" }[] | undefined>(undefined);
    const [tag_stats_date_count, setTagStatsDateCount] = useState<IBarDateChartData[] | undefined>(undefined);
    const [date_count, setDateCount] = useState<IBarDateChartData[] | undefined>(undefined);
    const [operation_user_count, setOperationUserCount] = useState<{ user_full_name: string, count: number }[] | undefined>(undefined);
    const [confirmation_user_count, setConfirmationUserCount] = useState<{ user_full_name: string, count: number }[] | undefined>(undefined);
    const [onboarding_progress, setOnboardingProgress] = useState<number | undefined>(undefined);
    const [milestones_in_progress, setMilestonesInProgress] = useState<number | undefined>(undefined);
    const [milestones_completed, setMilestonesCompleted] = useState<number | undefined>(undefined);
    const [total_milestones, setTotalMilestones] = useState<number | undefined>(undefined);
    const [jobs_count, setJobsCount] = useState<number | undefined>(undefined);
    const [time_saved, setTimeSaved] = useState<number | undefined>(undefined);
    const [jobs_date_count, setJobsDateCount] = useState<IBarDateChartData[] | undefined>(undefined);

    useEffect(() => {
        const limit_by_range = onboarding?.step !== "build" && onboarding?.step !== "onboarding";
        setExamplesCount(getExamplesCount(example_stats.example_tags, onboarding));
        setExamplesUserCount(getExampleUserCount(example_stats.user_stats, limit_by_range ? range : "all"));
        setExamplesTagsCount(getTagsCount(example_stats.tag_stats, limit_by_range ? range : "all", onboarding));
        setTagStatsDateCount(getTagStatsDateCount(example_stats.tag_stats, range, onboarding));
        setDateCount(getDateCount(example_stats.example_stats, range));
        setOnboardingProgress(onboarding ? getOnboardingProgressStats(example_stats.example_tags, onboarding) : undefined);
    }, [example_stats, range, onboarding]);

    useEffect(() => {
        if (onboarding?.step === "operation" && onboarding.operation !== undefined) {
            setOperationUserCount(getOperationEndpointUserCount(operation_stats.endpoint_user_stats, range, false, onboarding));
            setConfirmationUserCount(getOperationEndpointUserCount(operation_stats.confirmation_user_stats, range, true, onboarding));
            const { jobs_date_count, jobs_count } = getEndpointJobsDateCount(operation_stats.endpoint_job_stats, range, onboarding);
            setJobsDateCount(jobs_date_count);
            setJobsCount(jobs_count);
            const { time_saved } = getEndpointJobsTimeSaved(operation_stats.confirmation_user_stats, range, onboarding);
            setTimeSaved(time_saved);
        } else {
            setOperationUserCount(getExampleUserCount(operation_stats.user_stats, range));
            setConfirmationUserCount(getOperationEndpointUserCount(operation_stats.confirmation_user_stats, range, true));
            const { jobs_date_count, jobs_count } = getJobsDateCount(operation_stats.job_stats, range);
            setJobsDateCount(jobs_date_count);
            setJobsCount(jobs_count);

        }
    }, [operation_stats, range, onboarding]);

    useEffect(() => {
        const milestones = onboarding?.step === "build" ? onboarding.build.milestones :
            onboarding?.step === "onboarding" && onboarding.onboarding !== undefined ? onboarding.onboarding.milestones :
                [];
        setMilestonesInProgress(milestones.filter(m => m.status === "in_progress").length);
        setMilestonesCompleted(milestones.filter(m => m.status === "completed").length);
        // must be at least 1 to avoid division by 0
        setTotalMilestones(Math.max(milestones.length, 1));
    }, [onboarding]);

    const total_examples = examples_user_count?.reduce((acc, user) => acc + user.count, 0) ?? 0;
    const examples_per_user = onboarding?.step === "onboarding" && onboarding.onboarding !== undefined ? onboarding.onboarding.no_examples_per_user : undefined;
    const required_examples = onboarding?.step === "build" ? onboarding.build.no_examples :
        onboarding?.step === "onboarding" && onboarding.onboarding !== undefined ?
            onboarding.onboarding.no_examples_per_user * onboarding.onboarding.responsibilities.users_uuids.length :
            0;

    const show_operation_stats = onboarding?.step !== "build" && onboarding?.step !== "onboarding";
    const expected_jobs_per_month = sumArray(onboarding?.operation?.expectations.filter(e => e.type === "operation").map(e => e.expected_no_jobs) ?? []);
    const expected_jobs_per_range = Math.round(expected_jobs_per_month * (range === "7d" ? 7 / 30 : 1));

    return <div className="grid grid-cols-3 gap-8">
        <div className="flex flex-col gap-8 row-span-2">

            {!show_operation_stats && <div className="flex flex-col gap-4">
                <h2 className="text-base font-semibold leading-7 text-gray-600 pl-4">User by new jobs</h2>
                {examples_user_count && <table className="min-w-full border border-gray-200 divide-y divide-gray-200 overflow-y-scroll">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-3 py-2 text-left text-sm font-semibold text-gray-900">User</th>
                            <th scope="col" className="px-3 py-2 text-left text-sm font-semibold text-gray-900">Count</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {examples_user_count.map((user, user_idx) => <tr key={user_idx}>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">{user.user_full_name}</td>
                            <td className={classNames(
                                "whitespace-nowrap px-3 py-2 text-sm text-gray-500",
                                examples_per_user ? (user.count < examples_per_user ? "text-red-500" : "text-mint-600") : "text-gray-500"
                            )}>
                                {user.count}
                                {examples_per_user && ` / ${examples_per_user}`}
                            </td>
                        </tr>)}
                    </tbody>
                    {onboarding?.step === "build" && <tfoot className={classNames(total_examples === onboarding.build.no_examples ? "bg-mint-50" : "bg-space_blue-50")}>
                        <tr>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">Total</td>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                                {total_examples} / {onboarding.build.no_examples}
                            </td>
                        </tr>
                    </tfoot>}
                </table>}
                {!examples_user_count && <LoadingSpinnerLimit />}
            </div>}

            {show_operation_stats && <div className="flex flex-col gap-4">
                <h2 className="text-base font-semibold leading-7 text-gray-600 pl-4">Jobs per user</h2>
                {operation_user_count && <table className="min-w-full border border-gray-200 divide-y divide-gray-200 overflow-y-scroll">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-3 py-2 text-left text-sm font-semibold text-gray-900">User</th>
                            <th scope="col" className="px-3 py-2 text-left text-sm font-semibold text-gray-900">Count</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {operation_user_count.map((user, user_idx) => <tr key={user_idx}>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">{user.user_full_name}</td>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">{user.count}</td>
                        </tr>)}
                    </tbody>
                    {onboarding?.step === "build" && <tfoot className={classNames(total_examples === onboarding.build.no_examples ? "bg-mint-50" : "bg-space_blue-50")}>
                        <tr>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">Total</td>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                                {total_examples} / {onboarding.build.no_examples}
                            </td>
                        </tr>
                    </tfoot>}
                </table>}
                {!operation_user_count && <LoadingSpinnerLimit />}
            </div>}

            {show_operation_stats && <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4">
                    <h2 className="text-base font-semibold leading-7 text-gray-600 pl-4">TODOs per user</h2>
                </div>
                {confirmation_user_count && <table className="min-w-full border border-gray-200 divide-y divide-gray-200 overflow-y-scroll">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-3 py-2 text-left text-sm font-semibold text-gray-900">User</th>
                            <th scope="col" className="px-3 py-2 text-left text-sm font-semibold text-gray-900">Count</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {confirmation_user_count.map((user, user_idx) => <tr key={user_idx}>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">{user.user_full_name}</td>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">{user.count}</td>
                        </tr>)}
                    </tbody>
                </table>}
                {!confirmation_user_count && <LoadingSpinnerLimit />}
            </div>}

            <div className="flex flex-col gap-4">
                <h2 className="text-base font-semibold leading-7 text-gray-600 pl-4">Top example tags</h2>
                {examples_tags_count && <table className="min-w-full border border-gray-200 divide-y divide-gray-200 overflow-y-scroll">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-3 py-2 text-left text-sm font-semibold text-gray-900">Tag</th>
                            <th scope="col" className="px-3 py-2 text-left text-sm font-semibold text-gray-900">Count</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {examples_tags_count.map((tag, tag_idx) => <tr key={tag_idx}>
                            <td className={classNames(
                                "whitespace-nowrap px-3 py-2 text-sm",
                                tag.color === "green" ? "text-mint-600" : tag.color === "red" ? "text-torch_red-600" : "text-gray-500"
                            )}>{tag.tag}</td>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">{tag.count}</td>
                        </tr>)}
                    </tbody>
                </table>}
                {!examples_tags_count && <LoadingSpinnerLimit />}
            </div>
        </div>

        <div className="flex flex-col gap-8 col-span-2">
            {!show_operation_stats && <Fragment>
                <div className="flex flex-col gap-4">
                    <h2 className="text-base font-semibold leading-7 text-gray-600 pl-4">Progress and examples</h2>
                    <div className="grid grid-cols-2 gap-4">
                        {examples_count && <Fragment>
                            <Indicator chart={{ data_type: "numbers", data: { "Progress": 100 * (onboarding_progress ?? 0), }, chart_type: "numbers", decimals: 0, color: (onboarding_progress ?? 0) < 1.0 ? "blue" : "green", suffix: "%", dynamic_filters: [] }} />
                            <Indicator chart={{ data_type: "numbers", data: { "Collected Examples": examples_count.total, }, chart_type: "numbers", decimals: 0, color: total_examples < required_examples ? "red" : "green", suffix: ` / ${required_examples}`, dynamic_filters: [] }} />
                            <Indicator chart={{ data_type: "numbers", data: { "Examples In Progress": examples_count.red, }, chart_type: "numbers", decimals: 0, color: "blue", dynamic_filters: [] }} />
                            <Indicator chart={{ data_type: "numbers", data: { "Correct Examples ": examples_count.green, }, chart_type: "numbers", decimals: 0, color: "green", dynamic_filters: [] }} />
                        </Fragment>}
                        {milestones_in_progress !== undefined && milestones_completed !== undefined && total_milestones !== undefined && <Fragment>
                            <Indicator chart={{ data_type: "numbers", data: { "Milestones In Progress": milestones_in_progress, }, chart_type: "numbers", decimals: 0, color: "blue", suffix: ` / ${total_milestones}`, dynamic_filters: [] }} />
                            <Indicator chart={{ data_type: "numbers", data: { "Milestones Completed": milestones_completed, }, chart_type: "numbers", decimals: 0, color: "green", suffix: ` / ${total_milestones}`, dynamic_filters: [] }} />
                        </Fragment>}
                    </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4 flex flex-col gap-4">
                    <div className="flex flex-row items-center gap-2">
                        <h2 className="text-base font-semibold leading-7 text-gray-600 text-center">Examples per day</h2>
                        <div className="flex-grow" />
                        <ButtonGroup
                            tiny={true}
                            buttons={[
                                { text: "7 days", selected: range === "7d", onClick: () => setRange("7d") },
                                { text: "30 days", selected: range === "30d", onClick: () => setRange("30d") }
                            ]}
                        />
                    </div>
                    {date_count && <BarChart data={date_count} aspect={3} />}
                    {!date_count && <LoadingSpinnerLimit />}
                </div>
            </Fragment>}

            {show_operation_stats && <Fragment>
                <div className="flex flex-col gap-4">
                    <div className="flex flex-row items-center gap-2">
                        <h2 className="text-base font-semibold leading-7 text-gray-600 pl-4">Usage and Benefits</h2>
                        <div className="flex-grow" />
                        <ButtonGroup
                            tiny={true}
                            buttons={[
                                { text: "7 days", selected: range === "7d", onClick: () => setRange("7d") },
                                { text: "30 days", selected: range === "30d", onClick: () => setRange("30d") }
                            ]}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        {onboarding?.step === "operation" && onboarding?.operation !== undefined && jobs_count !== undefined && time_saved !== undefined && <Fragment>
                            <Indicator chart={{ data_type: "numbers", data: { [`Jobs in last ${range === "7d" ? "7 days" : "30 days"}`]: jobs_count, }, chart_type: "numbers", decimals: 0, color: jobs_count < expected_jobs_per_range ? "red" : "black", suffix: ` / ${expected_jobs_per_range}`, dynamic_filters: [] }} />
                            <Indicator chart={{ data_type: "numbers", data: { [`Time Saved over last ${range === "7d" ? "7 days" : "30 days"}`]: getTimeSavedVal(time_saved), }, chart_type: "numbers", decimals: 1, color: "black", suffix: getTimeSavedUnit(time_saved), dynamic_filters: [] }} />
                        </Fragment>}
                        {onboarding === undefined && jobs_count !== undefined && <Indicator chart={{ data_type: "numbers", data: { [`Jobs in last ${range === "7d" ? "7 days" : "30 days"}`]: jobs_count, }, chart_type: "numbers", decimals: 0, color: "black", dynamic_filters: [] }} />}
                    </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4 flex flex-col gap-4">
                    <h2 className="text-base font-semibold leading-7 text-gray-600 text-center">Jobs per day</h2>
                    {jobs_date_count && jobs_date_count.length > 0 && <BarChart data={jobs_date_count} aspect={3} />}
                    {jobs_date_count && jobs_date_count.length === 0 && <div className="text-gray-500 text-center">No jobs in the last {range === "7d" ? "7 days" : "30 days"}</div>}
                    {!jobs_date_count && <LoadingSpinnerLimit />}
                </div>
                <div className="border border-gray-200 rounded-lg p-4 flex flex-col gap-4 col-span-2">
                    <h2 className="text-base font-semibold leading-7 text-gray-600 text-center">Status of reported issues</h2>
                    {tag_stats_date_count && <StackedBarChart data={tag_stats_date_count} aspect={3} values_type="onboarding" />}
                    {!tag_stats_date_count && <LoadingSpinnerLimit />}
                </div>
            </Fragment>}
        </div>
    </div>;
}